import { toCSSObject } from 'client/widget-components/responsive-styles/toCSSObject';
import { useCallback, useMemo } from 'react';
import { BookerResponsiveStyles, DesignStyle } from '../types';

// Read straight off `common`, so these have to stay kebab: unlike the text styles below, colors never pass
// through toCSSObject's camel-case normalization. A camelCase producer would silently fall back to the theme.
const BACKGROUND_COLOR = 'background-color';
const COLOR = 'color';

export interface BookerTheme {
    bookerPrimaryColor: string;
    bookerSecondaryColor: string;
    bookerBackgroundColor: string;
    bookerStyles: BookerResponsiveStyles;
    evaluateStyles: ({
        styleKey,
    }: {
        styleKey: keyof BookerResponsiveStyles;
    }) => ReturnType<typeof toCSSObject> | '';
}

const basicColors = {
    bookerPrimaryColor: '#272b35',
    bookerSecondaryColor: '#EDEFF1',
    bookerBackgroundColor: '#ffffff',
};

const themeColors = {
    bookerPrimaryColor: 'var(--color_1)',
    bookerSecondaryColor: 'var(--color_4)',
    bookerBackgroundColor: 'var(--color_3)',
};

export const useBookerColors = (
    _styles: BookerResponsiveStyles | undefined,
    designStyle: DesignStyle
): BookerTheme => {
    const theme = useMemo(() => {
        const bookerPrimaryColorCommon = _styles?.bookerPrimaryColor?.common as
            | Record<string, string>
            | undefined;
        const bookerSecondaryColorCommon = _styles?.bookerSecondaryColor
            ?.common as Record<string, string> | undefined;
        const bookerBackgroundColorCommon = _styles?.bookerBackgroundColor
            ?.common as Record<string, string> | undefined;

        let primaryColor = bookerPrimaryColorCommon?.[COLOR];
        let secondaryColor = bookerSecondaryColorCommon?.[BACKGROUND_COLOR];
        let backgroundColor = bookerBackgroundColorCommon?.[BACKGROUND_COLOR];

        const isBasicTheme = designStyle === DesignStyle.BASIC;
        const fallback = isBasicTheme ? basicColors : themeColors;

        if (!primaryColor) {
            primaryColor = fallback.bookerPrimaryColor;
        }
        if (!secondaryColor) {
            secondaryColor = fallback.bookerSecondaryColor;
        }
        if (!backgroundColor) {
            backgroundColor = fallback.bookerBackgroundColor;
        }

        return {
            bookerPrimaryColor: primaryColor,
            bookerSecondaryColor: secondaryColor,
            bookerBackgroundColor: backgroundColor,
            bookerStyles: _styles ?? {},
        };
    }, [_styles, designStyle]);

    const evaluateStyles = useCallback(
        ({ styleKey }: { styleKey: keyof BookerResponsiveStyles }) => {
            const styles = theme.bookerStyles?.[styleKey];
            if (!styles) {
                return '';
            }
            return toCSSObject(styles, false, true);
        },
        [theme]
    );

    return { ...theme, evaluateStyles };
};
